import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import Button from 'components/actions/Button';
import Can from 'components/auth/Can';
import NoData from 'components/content/NoData';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemFleetsQuery } from 'services/systemAdmin/fleets';
import { useListUsersQuery } from 'services/user';
import { useSummaryVehiclesV2Query } from 'services/vehiclesV2';
import {
    ActiveSidebar,
    clearActiveElements,
    getActiveSidebar,
    getSelectedVehicleVin,
    setVehicleDialog,
} from 'store/slices/settingsPageSlice';

import type { SectionItem } from '../SidebarSection';
import SystemVehiclesSidebarContent from './SystemVehiclesSidebarContent';
import { formatDateTimeWithDash, getVehicleTypeName, isVehicleStale } from './systemVehicleUtils';

interface FooterProps {
    openVehicleDialog(): void;
}

const Footer = ({ openVehicleDialog }: FooterProps) => (
    <Can type='fleets' action='edit'>
        <div className='padding-10'>
            <Button variant='outline' style='primary' className='Sidebar__button' onClick={openVehicleDialog}>
                <span className='rioglyph rioglyph-pencil mr-5' />
                Edit
            </Button>
        </div>
    </Can>
);

const SystemVehiclesSidebar = () => {
    const dispatch = useDispatch();
    const activeSidebar = useSelector(getActiveSidebar);
    const selectedVehicleVin = useSelector(getSelectedVehicleVin);

    const { data: fleets = [] } = useListSystemFleetsQuery('ListFleets');
    const { data: vehicles = [] } = useSummaryVehiclesV2Query();
    const { data: users = [] } = useListUsersQuery('ListUsers');

    const selectedVehicle = vehicles?.find(v => v.vin === selectedVehicleVin);
    const selectedFleet = fleets.find(fleet => fleet.id === selectedVehicle?.fleetId);
    const creator = (users ?? []).find(u => u.id === selectedVehicle?.createdBy);

    const closeSidebar = () => {
        dispatch(clearActiveElements());
    };

    const vehicleDetailsSectionItems: SectionItem[] = [
        {
            label: 'License Plate',
            element: selectedVehicle?.licensePlate ?? <NoData text='No data' className='text-size-14' />,
        },
        {
            label: 'VIN',
            element: selectedVehicle?.vin ?? <NoData text='No data' className='text-size-14' />,
        },
        {
            label: 'Type',
            element: selectedVehicle ? (
                getVehicleTypeName(selectedVehicle)
            ) : (
                <NoData text='No data' className='text-size-14' />
            ),
        },
        {
            label: 'Assigned Fleet',
            element: selectedFleet?.name ?? <NoData text='No data' className='text-size-14' />,
        },
    ];

    const createdBySectionItems: SectionItem[] = [
        {
            label: 'Created',
            element: selectedVehicle?.createdAt ? (
                formatDateTimeWithDash(selectedVehicle?.createdAt)
            ) : (
                <NoData text='No data' className='text-size-14' />
            ),
        },
        {
            label: 'Created by',
            element: [creator?.firstName, creator?.lastName].filter(Boolean).join(' ') || (
                <NoData text='No data' className='text-size-14' />
            ),
        },
    ];

    const openVehicleDialog = () => {
        if (selectedVehicle) {
            dispatch(setVehicleDialog({ status: 'open', mode: 'update', vehicle: selectedVehicle }));
        }
    };

    return (
        <Sidebar
            className='width-400'
            title={<p className='text-bold text-size-16 line-height-16 text-color-darkest'>Vehicle information</p>}
            titleClassName='pl-10'
            footer={<Footer openVehicleDialog={openVehicleDialog} />}
            closed={activeSidebar !== ActiveSidebar.VEHICLES}
            onClose={closeSidebar}
            fly
        >
            <SystemVehiclesSidebarContent
                vehicleName={selectedVehicle?.name ?? ''}
                isStale={selectedVehicle ? (isVehicleStale(selectedVehicle) ?? false) : false}
                vehicleDetailsSectionItems={vehicleDetailsSectionItems}
                createdBySectionItems={createdBySectionItems}
            />
        </Sidebar>
    );
};

export default SystemVehiclesSidebar;
